import React, { useEffect, useState } from "react"
import { useRequireAuthentication } from "hooks/useRequireAuthentication"
import { Redirect, Route, Switch, useHistory, useParams } from "react-router-dom"
import { ErrorBoundary } from "react-error-boundary"
import { ErrorDisplay } from "components/molecules/ErrorDisplay"
import { GetApplicationCohorts } from "lib/Applications"
import { SetCookie } from "lib/Cookie"
import { StatusPage, StatusPageNavSelected } from "./Status"
import Smartlook from "lib/Smartlook"
import RedirectPage from "./Redirect"
import { useApplicationSummaryEnhanced } from "hooks/useApplicationSummaryEnhanced"
import { useApplicationSummaries } from "hooks/useApplicationSummaries"
import { LoadingSpinner } from "pages/Application/Status/EligibilityLoader"

export default function () {
  useRequireAuthentication()
  const { friendlyId } = useParams<{ friendlyId: string }>()
  const history = useHistory()
  const { hasFetchedSummaries } = useApplicationSummaries()
  const { application: salesforceApplication } = useApplicationSummaryEnhanced({ friendlyId })

  useEffect(() => {
    if (salesforceApplication) {
      SetCookie("CohortsCookie", GetApplicationCohorts(salesforceApplication).join("."))
    }
  }, [hasFetchedSummaries, salesforceApplication])

  useEffect(() => {
    Smartlook((smartLook) => {
      smartLook.track("properties", { friendlyId })
    })
  }, [friendlyId])

  useEffect(() => {
    if (hasFetchedSummaries && !salesforceApplication) {
      history.push("/dashboard")
    }
  }, [hasFetchedSummaries, salesforceApplication])

  if (!hasFetchedSummaries) {
    return <LoadingSpinner />
  }

  return (
    <>
      <ErrorBoundary
        FallbackComponent={({ error, resetErrorBoundary }) => (
          <ErrorDisplay resetError={resetErrorBoundary}>{error.message}</ErrorDisplay>
        )}
      >
        <Switch>
          <Route exact path="/application/:friendlyId/">
            <Redirect to={`/application/${friendlyId}/eligibility`} />
          </Route>
          <Route path="/application/:friendlyId/eligibility">
            <Redirect to={`/application/${friendlyId}/status`} />
          </Route>
          <Route exact path="/application/:friendlyId/payment-confirmed">
            <StatusPage
              friendlyId={friendlyId}
              opportunityStage={salesforceApplication?.stage}
              selected={StatusPageNavSelected.Status}
              servicePaid={true}
            />
          </Route>
          <Route exact path="/application/:friendlyId/status">
            <StatusPage
              friendlyId={friendlyId}
              opportunityStage={salesforceApplication?.stage}
              selected={StatusPageNavSelected.Status}
            />
          </Route>
          <Route exact path="/application/:friendlyId/recommendation">
            <StatusPage
              friendlyId={friendlyId}
              opportunityStage={salesforceApplication?.stage}
              selected={StatusPageNavSelected.Recommendation}
            />
          </Route>
          <Route exact path="/application/:friendlyId/rate-check-promise">
            <StatusPage
              friendlyId={friendlyId}
              opportunityStage={salesforceApplication?.stage}
              selected={StatusPageNavSelected.RateChecker}
            />
          </Route>
          <Route exact path="/application/:friendlyId/documents">
            <StatusPage
              friendlyId={friendlyId}
              opportunityStage={salesforceApplication?.stage}
              selected={StatusPageNavSelected.Documents}
            />
          </Route>
          <Route path={"/application/:friendlyId/conveyancing"}>
            <Route exact path="/application/:friendlyId/conveyancing">
              <StatusPage
                friendlyId={friendlyId}
                opportunityStage={salesforceApplication?.stage}
                selected={StatusPageNavSelected.Conveyancing}
              />
            </Route>
          </Route>
          <Route path={"/application/:friendlyId/redirect"}>
            <RedirectPage />
          </Route>
          <Redirect to={`/application/${friendlyId}`} />
        </Switch>
      </ErrorBoundary>
    </>
  )
}
